/* eslint-disable sonarjs/no-duplicate-string */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  colors,
  breakpoints,
} from "@mytutor/shared-react-web-components/lib/theme";
import { css } from "@emotion/react";
import Image, { ImageLoaderProps } from "next/image";
import { mediaQueries } from "../../util/sharedStyles/mediaQueries";
import { Box, Card, Stack, Tooltip, Typography } from "@mui/material";
import TutorCardName from "../TutorCardName";
import { TutorType } from "../../util/fetchTutors/types";
import TutorHighlightedReview, {
  TutorHighlightedReviewProps,
} from "../TutorHighlightedReview";
import TimeAgo from "javascript-time-ago";
import en from "javascript-time-ago/locale/en.json";

TimeAgo.addDefaultLocale(en);

async function fetchFeedback() {
  const response = await fetch(`/constants/${process.env.NEXT_PUBLIC_TUTOR_REVIEWS_JSON}.json`);
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.json();
}

const tutorCardStyle = css`
  .main-row {
    display: flex;
  }

  .main-row-left {
    flex: 9;
    padding-left: 20px;
    padding-right: 20px;
    max-width: 100%;

    @media (min-width: ${breakpoints.tablet}px) {
      padding: 0;
      max-width: auto;
    }
    @media (min-width: ${breakpoints.desktop}px) {
      padding-right: 1.5em;
    }
  }

  .tutor-card-link {
    display: block;
    max-width: 100%;
  }

  .tutor-card {
    margin: auto;
    margin-bottom: 32px;
    padding: 24px;
    background: #fff;
    cursor: pointer;
    box-shadow: 0 8px 16px 0 rgb(63 46 28 / 7%);
    border-radius: 8px;
    transition-property: box-shadow;
    transition-duration: 0.15s;
    transition-timing-function: ease-in-out;
    max-width: 100%;
    :hover {
      box-shadow: 0 6px 16px 0 rgb(84 81 116 / 20%);
    }
    @media (min-width: ${breakpoints.tablet}px) {
      margin-bottom: 16px;
      padding: 0;
      width: 100%;
    }
  }

  .tutor-card__image-wrap {
    width: 110px;
    height: 110px;
    border-radius: 8px;
    position: relative;

    @media (min-width: ${breakpoints.tablet}px) {
      width: 100%;
      max-width: 190px;
      height: auto;
    }
  }

  .highlighted-review {
    display: none;
  }

  .tutor-card__image {
    object-fit: cover;
    border-radius: 8px;

    @media (min-width: ${breakpoints.tablet}px) {
      border-radius: 0;
    }
  }

  .tutor-card-body {
    display: flex;
    flex: 4;
  }

  .tutor-card-details {
    flex: 3;
    padding: 20px;
    display: none;

    @media (min-width: ${breakpoints.tablet}px) {
      display: block;
    }
  }

  .tutor-card-details--mobile {
    margin-top: 24px;
    max-width: 100%;

    @media (min-width: ${breakpoints.tablet}px) {
      display: none;
    }
  }

  .tutor-card-info {
    flex: 1;
    min-width: 120px;
    max-width: 120px;
    margin-left: 16px;

    @media (min-width: ${breakpoints.tablet}px) {
      border-left: solid 1px ${colors.bgcolor1};
      padding: 20px;
      min-width: 185px;
      max-width: 185px;
      min-height: 166px;
    }
  }

  .no-tutor-card-container {
    display: flex;
    justify-content: center;

    .aside-cta {
      @media (min-width: ${breakpoints.desktop}px) {
        max-width: 560px;
      }
    }
  }
`;

const cdnLoader = ({ src, width, quality }: ImageLoaderProps) => {
  return `${process.env.NEXT_PUBLIC_CDN_URL}/fleet${src}?w=${width}&q=${quality || 75}`;
};

type TutorCardProps = {
  tutorsList: TutorType[] | null;
  sidebarCTA: React.ReactElement;
};

type TutorInfo = Pick<
  TutorType,
  "id" | "firstName" | "numberOfLessons" | "numberOfRatings"
>;
type TutorHighlightedReviewInfo = Omit<
  TutorHighlightedReviewProps,
  "containerRef"
>;

const isNewTutor = (numberOfLessons: number) => numberOfLessons < 10;

const TutorCard = ({ tutorsList, sidebarCTA }: TutorCardProps) => {
  const [feedbackData, setFeedbackData] = useState([]);

  useEffect(() => {
    fetchFeedback().then(data => setFeedbackData(data)).catch(error => console.error('Error fetching feedback:', error));
  }, []);

  const ref = useRef<HTMLDivElement>(null);

  const [hoveredTutorInfo, setHoveredTutorInfo] =
    useState<TutorHighlightedReviewInfo>(null);

  const handleSetHoveredTutorInfo = useCallback(
    (tutor?: TutorInfo, index?: number) => {
      const tutorInfo = tutor || tutorsList?.[0];

      if (!tutorInfo) return;

      if (isNewTutor(tutorInfo?.numberOfLessons)) {
        setHoveredTutorInfo(null);
        return;
      }

      const tutorFeedbackData = feedbackData.find(
        (tutorFeedback) =>
          String(tutorFeedback?.tutorId) === String(tutorInfo?.id)
      );

      setHoveredTutorInfo(
        tutorFeedbackData
          ? {
              ...tutorFeedbackData,
              tutorId: tutorInfo?.id,
              tutorName: tutorInfo?.firstName,
              position: index ?? 0,
            }
          : null
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tutorsList]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setHoveredTutorInfo(null);
      }
    };
    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // if pagination is used, we need to reset the hovered tutor info
    handleSetHoveredTutorInfo();
  }, [handleSetHoveredTutorInfo]);

  const showTutorsList = () => {
    return tutorsList?.map(
      (
        {
          id,
          firstName,
          lastName,
          description,
          profilePictureFilePath,
          university,
          degreeType,
          degreeCourse,
          price,
          rating,
          numberOfRatings,
          numberOfLessons,
          labels,
        },
        i
      ) => (
        <a
          onMouseEnter={() => {
            handleSetHoveredTutorInfo(
              { id, firstName, numberOfRatings, numberOfLessons },
              i
            );
          }}
          className="tutor-card-link"
          href={process.env.NEXT_PUBLIC_ZEUS_URL + `/tutors/${id}`}
          rel="nofollow"
          key={id}
          onClick={(e) => {
            e.preventDefault();

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: "select_item",
              ecommerce: {
                items: [
                  {
                    item_name: (firstName + "_" + lastName).toLowerCase(),
                    item_id: id,
                    price: null,
                    item_brand: "mytutor",
                    item_category: null,
                    item_category2: null,
                    item_variant: null,
                    item_list_name: "/view-tutors",
                    index: i,
                    quantity: 1,
                    user_type: null,
                  },
                ],
              },
            });
            window.location.href =
              process.env.NEXT_PUBLIC_ZEUS_URL + `/tutors/${id}`;
          }}
        >
          <Card className="tutor-card" key={id}>
            <Stack direction="row">
              <div className="tutor-card__image-wrap">
                {profilePictureFilePath ? (
                  <Image
                    loader={cdnLoader}
                    src={profilePictureFilePath}
                    alt="Tutor Image"
                    className={"tutor-card__image"}
                    fill={true}
                  />
                ) : (
                  <Image
                    src="/view-tutors/profile.svg"
                    alt="Tutor Image"
                    className={"tutor-card__image"}
                    fill={true}
                  />
                )}
              </div>
              <div className="tutor-card-body">
                <div className="tutor-card-details">
                  <TutorCardName
                    firstName={firstName}
                    lastName={lastName}
                    labels={labels}
                  />
                  <Typography
                    variant="text2"
                    mb="8px"
                    sx={{
                      display: "-webkit-box",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: "1",
                    }}
                  >
                    {university}
                    {" - "}
                    {degreeCourse} ({degreeType})
                  </Typography>
                  <Typography
                    variant="text2"
                    color="text.secondary"
                    sx={{
                      display: "-webkit-box",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      marginBottom: "12px",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: "2",
                    }}
                  >
                    {description}
                  </Typography>
                </div>
                <div className="tutor-card-info">
                  <Typography variant="h3" mb="8px">
                    {price}{" "}
                    <Typography
                      variant="text1"
                      sx={{ display: "inline" }}
                      color="text.secondary"
                    >
                      /hr
                    </Typography>
                  </Typography>
                  {numberOfRatings > 0 &&
                    <Typography
                      variant="text2"
                      color="text.secondary"
                      sx={{ marginBottom: { xs: "4px", md: "8px" } }}
                    >
                      <Image
                        src="/view-tutors/start-rating.svg"
                        alt="Star"
                        height={14}
                        width={14}
                      />{" "}
                      {rating} / 5
                    </Typography>
                  }
                  {isNewTutor(numberOfLessons) ? (
                    <Box data-cy="new-tutor-badge" sx={{ display: "flex" }}>
                      <Tooltip
                        arrow
                        leaveTouchDelay={4000}
                        enterTouchDelay={0}
                        placement="bottom"
                        slotProps={{
                          arrow: { sx: { color: "#242424" } },
                          tooltip: {
                            sx: {
                              maxWidth: "260px",
                              fontWeight: 400,
                              padding: "16px",
                            },
                          },
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        title="This tutor has recently become available to tutor privately."
                      >
                        <Box
                          sx={{ marginBottom: { xs: "4px", md: "8px" } }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="text2" color="text.secondary">
                            New tutor
                          </Typography>
                          <Image
                            style={{ marginLeft: "3px" }}
                            src={"/view-tutors/tooltip-hint.svg"}
                            alt="tooltip hint"
                            width={16}
                            height={16}
                          />
                        </Box>
                      </Tooltip>
                    </Box>
                  ) : (
                    numberOfRatings > 0 && (
                      <Typography
                        variant="text2"
                        color="text.secondary"
                        sx={{
                          marginBottom: { xs: "4px", md: "8px" },
                        }}
                      >
                        {numberOfRatings} reviews
                      </Typography>
                    )
                  )}
                  {numberOfLessons > 0 &&
                    <Typography
                      variant="text2"
                      color="text.secondary"
                      sx={{
                        marginBottom: { xs: "4px", md: "8px" },
                      }}
                    >
                      {numberOfLessons} lessons
                    </Typography>
                  }
                </div>
              </div>
            </Stack>
            <div className="tutor-card-details--mobile">
              <TutorCardName
                firstName={firstName}
                lastName={lastName}
                labels={labels}
              />
              <Typography
                variant="text2"
                mb="8px"
                sx={{
                  display: "-webkit-box",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: "1",
                }}
              >
                {university}
                {" - "}
                {degreeCourse} ({degreeType})
              </Typography>
              <Typography
                variant="text2"
                color="text.secondary"
                sx={{
                  display: "-webkit-box",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  marginBottom: "12px",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: "2",
                  maxWidth: "100%",
                  overflowWrap: "break-word",
                }}
              >
                {description}
              </Typography>
            </div>
          </Card>
        </a>
      )
    );
  };

  return (
    <main ref={ref} css={tutorCardStyle} id="tutors-list">
      <div className="row main-row" css={mediaQueries}>
        <div className="main-row-left">
          <div>
            {tutorsList?.length > 0 ? (
              showTutorsList()
            ) : (
              <aside className="no-tutor-card-container forDesktopFlex">
                {sidebarCTA}
              </aside>
            )}
          </div>
        </div>
        {/* we don't want the side dialog box to show if showNoTutorCard is visible */}
        {tutorsList?.length > 0 && (
          <aside className="forDesktopBlock" style={{ width: "340px" }}>
            {hoveredTutorInfo && (
              <TutorHighlightedReview
                {...hoveredTutorInfo}
                containerRef={ref}
              />
            )}
            {sidebarCTA}
          </aside>
        )}
      </div>
    </main>
  );
};

export default TutorCard;
